/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import { useQuery } from '@tanstack/react-query';
import { artifactService } from '../services';
import type { ArtifactTypeName } from '../types/artifact-usage';

export const ARTIFACT_USAGE_KEY = 'artifact-usage';

/**
 * **What points at this artifact** (`P4`, #1071).
 *
 * One key for every type, so a single `invalidateQueries([ARTIFACT_USAGE_KEY])` after any artifact
 * write refreshes every panel. That is the correctness property rather than a convenience: a
 * dependency answer that outlived a save would tell an operator nothing uses a source they had just
 * pointed a strategy at — the stale-claim shape (#999), with a delete button beside it.
 *
 * `enabled` gates on the id **and** on the caller's own flag, because the commonest use is a confirm
 * dialog: asking before the dialog opens spends a whole-graph read on a click that may never come.
 */
export function useArtifactUsage(type: ArtifactTypeName, id: string | number | undefined, enabled = true) {
  return useQuery({
    queryKey: [ARTIFACT_USAGE_KEY, type, id],
    queryFn: () => artifactService.usage(type, id!),
    enabled: Boolean(id) && enabled,
  });
}
